import format from "date-fns/format"
import isSameDay from "date-fns/isSameDay"
import parseISO from "date-fns/parseISO"
import PropTypes from "prop-types"
import React, { useEffect, useMemo, useState } from "react"
import { useMutation, useQuery } from "react-query"
import { useLocation, useNavigate } from "react-router-dom"
import {
  LaunchedActions,
  ReturnedActions,
  ScheduledActions,
} from "src/actions/schedule_appointments"
import { useMarina } from "src/contexts/MarinaContext"

import Button from "src/components/Button"
import DataTableV2 from "src/components/DataTableV2"
import ViewSwitcher from "src/components/DataTableV2/ViewSwitcher"
import { useResponsiveView } from "src/components/DataTableV2/useResponsiveView"
import Form from "src/components/Form"
import LongFormDatePicker from "src/components/LongFormDatePicker"
import Tabs from "src/components/Tabs"

import {
  getLaunchedAppointments,
  getScheduledAppointments,
  markAppointmentsAsViewed,
  queryReturnedLaunches,
  queryScheduleAppointmentCounts,
  queryScheduledLaunches,
  queryUnviewedScheduleAppointmentCounts,
  updateScheduleAppointmentBoaterStatus,
  updateScheduleAppointmentStatus,
} from "src/api/DryStack"

import useDatatable from "src/hooks/use_datatable"
import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"
import { useWindowSize } from "src/hooks/use_window_size"

import LaunchFuelQuantityModal from "./LaunchFuelQuantityModal"
import {
  AllLaunchesComplete,
  NoBoatsLaunched,
  NoLaunchesScheduled,
  NoRecentReturns,
} from "./MarinaScheduleBlankStates"
import {
  getLaunchedColumns,
  getReturnedColumns,
  getScheduledColumns,
} from "./MarinaScheduleColumns"
import { MarinaScheduleContext } from "./MarinaScheduleContext"
import { handleError } from "./MarinaScheduleSharedMethods"
import ScheduleAppointmentModal from "./ScheduleAppointmentModal"
import useLaunchCart from "./hooks/use_launch_cart"

const TAB_NAMES = ["scheduled", "launched", "returned"]

const MarinaScheduleView = ({
  canManage,
  launchAddonsEnabled,
  launchCartCopy,
  marinaTimezone,
  showCreateLaunchButton,
  trackingProps,
}) => {
  const { marinaSlug } = useMarina()
  const navigate = useNavigate()
  const location = useLocation()
  const showToast = useToast()
  const tracker = useTracker()

  // URL params
  const urlParams = new URLSearchParams(location.search)
  const currentDateFromParams = urlParams.get("date")
  const currentTabFromParams = urlParams.get("tab")
  const currentViewFromParams = urlParams.get("view")

  // Tab state
  const currentTabNumber = () => {
    switch (currentTabFromParams) {
      case "scheduled":
        return 0
      case "launched":
        return 1
      case "returned":
        return 2
      default:
        return 0
    }
  }
  const [currentTab, setCurrentTab] = useState(currentTabNumber())

  // Date/view state (local to scheduled tab)
  const [currentDate, setCurrentDate] = useState(
    currentDateFromParams || format(new Date(), "yyyy-MM-dd")
  )
  const [viewingAll, setViewingAll] = useState(currentViewFromParams === "all")

  const { isLargeScreen } = useWindowSize()
  const [viewOverride, setViewOverride] = useState(null)
  // The switcher is hidden below lg, so only apply an override where the control
  // is present to undo it — below lg the tables follow the responsive default.
  const appliedViewOverride = isLargeScreen ? viewOverride : null

  const handleViewSwitch = (view) => {
    setViewOverride(view)
    tracker.trackEvent("dry_stack_schedule_view:view_switched", {
      ...trackingProps,
      view,
    })
  }

  // Counts
  const [scheduledCount, setScheduledCount] = useState(0)
  const [allScheduledCount, setAllScheduledCount] = useState(0)
  const [launchedCount, setLaunchedCount] = useState(0)
  const [returnedCount, setReturnedCount] = useState(0)

  // Unviewed tracking
  const [allTimeUnviewedScheduledCount, setAllTimeUnviewedScheduledCount] =
    useState(0)
  const [dailyUnviewedScheduledCount, setDailyUnviewedScheduledCount] =
    useState(0)
  const [unviewedScheduledAppointmentIds, setUnviewedScheduledAppointmentIds] =
    useState(new Set())

  // Modal state
  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [currentAppointment, setCurrentAppointment] = useState(null)

  // Refresh trigger
  const [lastActionTakenAt, setLastActionTakenAt] = useState(new Date())
  const handleLastActionTakenAt = () => setLastActionTakenAt(new Date())

  // Unviewed mark-as-viewed
  const [markedAsViewed, setMarkedAsViewed] = useState(false)

  const viewIsToday = () => isSameDay(new Date(), parseISO(currentDate))

  const currentTabName = () => TAB_NAMES[currentTab]

  // --- URL sync on mount ---
  useEffect(() => {
    // Only sync URL for scheduled tab - launched/returned tabs don't use date/view params
    if (
      currentTabFromParams === "launched" ||
      currentTabFromParams === "returned"
    ) {
      return
    }

    if (!currentDateFromParams && !currentViewFromParams) {
      let queryParams = "?view=daily"
      setCurrentDate(format(new Date(), "yyyy-MM-dd"))
      if (!currentTabFromParams) {
        queryParams += "&tab=scheduled"
      }
      navigate(queryParams)
    } else if (!currentTabFromParams) {
      navigate(`?view=daily&tab=scheduled&date=${currentDateFromParams}`)
    }
  }, [
    currentDateFromParams,
    currentTabFromParams,
    currentViewFromParams,
    navigate,
  ])

  // --- Counts query ---
  useQuery(
    ["scheduleAppointmentsCount", marinaSlug, currentTab, lastActionTakenAt],
    () => queryScheduleAppointmentCounts(marinaSlug, true),
    {
      retry: false,
      refetchInterval: 60 * 5000,
      onSuccess: (data) => {
        if (data) {
          setLaunchedCount(data.launched)
          setReturnedCount(data.returned)
          setAllScheduledCount(data.allScheduled)
          setScheduledCount(data.scheduled)
        }
      },
    }
  )

  // --- Scheduled launches query (daily view) ---
  const {
    isLoading: scheduledDailyLoading,
    isError: scheduledDailyError,
    data: scheduledDailyData,
  } = useQuery(
    ["scheduledLaunches", marinaSlug, lastActionTakenAt, currentDate],
    () => queryScheduledLaunches(marinaSlug, currentDate),
    {
      retry: false,
      refetchInterval: 60 * 1000,
      enabled: currentTab === 0 && !viewingAll,
      onSuccess: (data) => {
        const unviewedIds = data.scheduleAppointments
          .filter((a) => !a.viewed)
          .map((a) => a.encodedId)
        setUnviewedScheduledAppointmentIds(
          new Set([...unviewedIds, ...unviewedScheduledAppointmentIds])
        )
      },
    }
  )

  // --- Scheduled "View All" (infinite scroll) ---
  const scheduledAllTable = useDatatable({
    queryKey: ["scheduledLaunchesAll", marinaSlug, lastActionTakenAt],
    fetchFn: getScheduledAppointments,
    idKey: "encodedId",
    infinite: true,
    syncFiltersToUrl: false,
    enabled: currentTab === 0 && viewingAll,
    refetchInterval: 60 * 1000,
  })

  const handleScheduledScroll = (e) => {
    if (!scheduledAllTable.hasNextPage || scheduledAllTable.isFetchingNextPage)
      return
    const { scrollHeight, scrollTop, clientHeight } = e.target
    if (scrollHeight - scrollTop - 200 <= clientHeight) {
      scheduledAllTable.fetchNextPage()
    }
  }

  // Highlight unviewed rows in "View All" using the loaded (paginated) pages
  useEffect(() => {
    if (currentTab !== 0 || !viewingAll) return
    const unviewedIds = scheduledAllTable.records
      .filter((a) => !a.viewed)
      .map((a) => a.encodedId)
    if (unviewedIds.length === 0) return
    setUnviewedScheduledAppointmentIds((prev) => {
      const next = new Set(prev)
      unviewedIds.forEach((id) => next.add(id))
      return next.size === prev.size ? prev : next
    })
  }, [scheduledAllTable.records, currentTab, viewingAll]) // eslint-disable-line react-hooks/exhaustive-deps

  // --- Unviewed counts query ---
  useQuery(
    [
      "unviewedScheduleAppointmentsCount",
      marinaSlug,
      viewingAll,
      currentTab,
      currentDate,
    ],
    () => queryUnviewedScheduleAppointmentCounts(marinaSlug, currentDate),
    {
      retry: false,
      refetchInterval: 60 * 1000,
      enabled: currentTab === 0,
      onSuccess: (countData) => {
        if (!countData) return
        if (viewingAll) {
          setAllTimeUnviewedScheduledCount(
            Math.max(
              countData.allTimeUnviewedCount,
              unviewedScheduledAppointmentIds.size
            )
          )
          setDailyUnviewedScheduledCount(countData.dailyUnviewedCount)
        } else {
          setDailyUnviewedScheduledCount(
            Math.max(
              countData.dailyUnviewedCount,
              unviewedScheduledAppointmentIds.size
            )
          )
          setAllTimeUnviewedScheduledCount(countData.allTimeUnviewedCount)
        }
        setMarkedAsViewed(true)
      },
    }
  )

  // --- Mark appointments as viewed ---
  useQuery(
    [
      "markAppointmentsAsViewed",
      marinaSlug,
      viewingAll,
      currentTab,
      currentDate,
    ],
    () => markAppointmentsAsViewed(marinaSlug, viewingAll ? null : currentDate),
    {
      enabled: markedAsViewed,
      onSuccess: () => setMarkedAsViewed(false),
    }
  )

  // --- Launched launches (infinite scroll) ---
  const launchedTable = useDatatable({
    queryKey: ["launchedLaunches", marinaSlug, lastActionTakenAt],
    fetchFn: getLaunchedAppointments,
    idKey: "encodedId",
    infinite: true,
    syncFiltersToUrl: false,
    enabled: currentTab === 1,
    refetchInterval: 60 * 1000,
  })

  const launchedError = launchedTable.isError

  const handleLaunchedScroll = (e) => {
    if (!launchedTable.hasNextPage || launchedTable.isFetchingNextPage) return
    const { scrollHeight, scrollTop, clientHeight } = e.target
    if (scrollHeight - scrollTop - 200 <= clientHeight) {
      launchedTable.fetchNextPage()
    }
  }

  // --- Returned launches query ---
  const {
    isLoading: returnedLoading,
    isError: returnedError,
    data: returnedData,
  } = useQuery(
    ["returnedLaunches", marinaSlug, lastActionTakenAt],
    () => queryReturnedLaunches(marinaSlug),
    {
      retry: false,
      refetchOnMount: false,
      refetchInterval: 60 * 1000,
      enabled: currentTab === 2,
    }
  )

  // --- Status update mutation ---
  const { mutate: updateStatus } = useMutation(
    (data) => updateScheduleAppointmentStatus(marinaSlug, data),
    {
      onSuccess: () => {
        showToast("Launch details updated", { type: "success" })
        handleLastActionTakenAt()
      },
      onError: (data) => handleError(data, showToast),
    }
  )

  // --- Boater status mutation ---
  const { mutate: updateBoaterStatus } = useMutation(
    ({ encodedId, boaterStatus }) =>
      updateScheduleAppointmentBoaterStatus(
        marinaSlug,
        encodedId,
        boaterStatus
      ),
    {
      onSuccess: () => {
        showToast("Boater status updated", { type: "success" })
        handleLastActionTakenAt()
      },
      onError: (data) => handleError(data, showToast),
    }
  )

  const handleBoaterStatusChange = (encodedId, boaterStatus) => {
    tracker.trackEvent(
      `dry_stack_schedule_view:${currentTabName()}_tab_boater_status_changed`,
      {
        ...trackingProps,
        boater_status: boaterStatus || "cleared",
      }
    )
    updateBoaterStatus({ encodedId, boaterStatus })
  }

  // --- Cancel mutation (for confirm action) ---
  const { mutate: cancelLaunch } = useMutation(
    (data) => updateScheduleAppointmentStatus(marinaSlug, data),
    {
      onSuccess: () => {
        showToast("Launch canceled", { type: "success" })
        handleLastActionTakenAt()
      },
      onError: (data) => handleError(data, showToast),
    }
  )

  const {
    buildCart,
    cancelFuelQuantities,
    confirmFuelQuantities,
    isBuildingCart,
    pendingFuelLaunch,
  } = useLaunchCart({
    marinaSlug,
    trackingProps,
    genericError: launchCartCopy.genericError,
  })

  // --- Track status update ---
  const trackStatusUpdate = (tabName, targetStatus) => {
    let eventName
    if (tabName === "scheduled") {
      eventName = "scheduled_tab_mark_launched"
    } else if (tabName === "launched") {
      if (targetStatus === "returned") {
        eventName = "launched_tab_mark_returned"
      } else if (targetStatus === "scheduled") {
        eventName = "launched_tab_back_to_scheduled"
      }
    } else if (tabName === "returned") {
      eventName = `returned_tab_back_to_${targetStatus}`
    }
    tracker.trackEvent(`dry_stack_schedule_view:${eventName}`, {
      ...trackingProps,
    })
  }

  // --- Action handlers ---
  const handleStatusChange = (row, targetStatus) => {
    trackStatusUpdate(TAB_NAMES[currentTab], targetStatus)
    updateStatus({ encodedId: row.encodedId, status: targetStatus })
  }

  const handleCancelLaunch = (row) => {
    tracker.trackEvent(
      `dry_stack_schedule_view:${TAB_NAMES[currentTab]}_tab_cancel_launch_confirmed`,
      { ...trackingProps }
    )
    cancelLaunch({ encodedId: row.encodedId, status: "canceled" })
  }

  const handleEditDetails = (row) => {
    tracker.trackEvent(
      `dry_stack_schedule_view:${TAB_NAMES[currentTab]}_tab_edit_launch_started`,
      { ...trackingProps }
    )
    setCurrentAppointment(row)
    setModalIsOpen(true)
  }

  // --- Tab actions ---
  const scheduledActions = useMemo(
    () =>
      ScheduledActions({
        onMarkLaunched: (row) => handleStatusChange(row, "launched"),
        onEdit: handleEditDetails,
        onCancel: handleCancelLaunch,
        onBuildCart: buildCart,
        canManage,
        launchAddonsEnabled,
        launchCartCopy,
        isBuildingCart,
      }),
    [canManage, launchAddonsEnabled, launchCartCopy, isBuildingCart] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const launchedActions = useMemo(
    () =>
      LaunchedActions({
        onMarkReturned: (row) => handleStatusChange(row, "returned"),
        onMoveToScheduled: (row) => handleStatusChange(row, "scheduled"),
        onBuildCart: buildCart,
        canManage,
        launchAddonsEnabled,
        launchCartCopy,
        isBuildingCart,
      }),
    [canManage, launchAddonsEnabled, launchCartCopy, isBuildingCart] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const returnedActions = useMemo(
    () =>
      ReturnedActions({
        onMoveToScheduled: (row) => handleStatusChange(row, "scheduled"),
        onMoveToLaunched: (row) => handleStatusChange(row, "launched"),
        canManage,
      }),
    [canManage] // eslint-disable-line react-hooks/exhaustive-deps
  )

  // --- Column definitions ---
  const scheduledColumns = useMemo(
    () =>
      getScheduledColumns({
        viewingAll,
        unviewedIds: unviewedScheduledAppointmentIds,
        onUpdateBoaterStatus: handleBoaterStatusChange,
        canManage,
        marinaSlug,
      }),
    [viewingAll, unviewedScheduledAppointmentIds, canManage, marinaSlug] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const launchedColumns = useMemo(
    () =>
      getLaunchedColumns({
        onUpdateBoaterStatus: handleBoaterStatusChange,
        canManage,
        marinaSlug,
      }),
    [canManage, marinaSlug] // eslint-disable-line react-hooks/exhaustive-deps
  )
  const returnedColumns = useMemo(
    () => getReturnedColumns({ marinaSlug }),
    [marinaSlug]
  )

  // Derive the switcher highlight from the same hook the tables use, so it can't
  // drift from what's actually rendered.
  const { shouldShowCards: launchesShowCards } = useResponsiveView(
    launchedColumns,
    viewOverride
  )
  const activeView = launchesShowCards ? "cards" : "table"

  // --- Tab handlers ---
  const handleTabChange = (tab) => {
    setCurrentTab(tab)
    if (tab === 0) {
      tracker.trackEvent("dry_stack_schedule_view:scheduled_tab_viewed", {
        ...trackingProps,
      })
      setViewingAll(false)
      setUnviewedScheduledAppointmentIds(new Set())
      navigate("?view=daily&tab=scheduled")
    } else if (tab === 1) {
      tracker.trackEvent("dry_stack_schedule_view:launched_tab_viewed", {
        ...trackingProps,
      })
      navigate("?tab=launched")
    } else {
      tracker.trackEvent("dry_stack_schedule_view:returned_tab_viewed", {
        ...trackingProps,
      })
      navigate("?tab=returned")
    }
  }

  const handleReportsClick = () => {
    tracker.trackEvent("dry_stack_schedule_view:launch_reports_viewed", {
      ...trackingProps,
    })
    navigate("launch_reports")
  }

  const handleNewLaunchClick = () => {
    setModalIsOpen(true)
  }

  // --- Scheduled tab view controls ---
  const handleViewChange = (value) => {
    if (value === "all") {
      setViewingAll(true)
      setUnviewedScheduledAppointmentIds(new Set())
      navigate("?view=all&tab=scheduled")
      tracker.trackEvent(
        "dry_stack_schedule_view:scheduled_launches_view_all_selected",
        { ...trackingProps }
      )
    } else {
      setViewingAll(false)
      setUnviewedScheduledAppointmentIds(new Set())
      navigate("?view=daily&tab=scheduled")
      tracker.trackEvent(
        "dry_stack_schedule_view:scheduled_launches_daily_view_selected",
        { ...trackingProps }
      )
    }
  }

  // --- Blank state navigation ---
  const handleNavigateToLaunched = () => {
    tracker.trackEvent(
      "dry_stack_schedule_view:scheduled_tab_blank_state_to_launched_tab",
      { ...trackingProps }
    )
    navigate("?tab=launched")
    setCurrentTab(1)
  }

  const handleNavigateToSettings = () => {
    tracker.trackEvent(
      "dry_stack_schedule_view:scheduled_tab_blank_state_to_settings",
      { ...trackingProps }
    )
    window.location.assign(`/manage/${marinaSlug}/settings/dry_stack`)
  }

  const scheduledLoading = viewingAll
    ? scheduledAllTable.isLoading
    : scheduledDailyLoading
  const scheduledError = viewingAll
    ? scheduledAllTable.isError
    : scheduledDailyError
  const scheduledRows = viewingAll
    ? scheduledAllTable.records
    : scheduledDailyData?.scheduleAppointments ?? []

  const launchedRows = launchedTable.records

  const returnedRows = returnedData?.scheduleAppointments ?? []

  // --- Render helpers ---
  const renderViewOption = (option, count) => (
    <div className="flex items-center justify-between pr-2">
      <span>{option}</span>
      {count > 0 && (
        <div className="rounded-full bg-blue-700 px-2 py-1 font-bold text-white">
          {count} New
        </div>
      )}
    </div>
  )

  const renderScheduledTab = () => {
    if (scheduledError)
      return (
        <div className="flex w-full justify-center p-4 lg:justify-start">
          Error loading launches
        </div>
      )

    const scheduledEmptyState = viewIsToday() ? (
      <AllLaunchesComplete onNavigateToLaunched={handleNavigateToLaunched} />
    ) : (
      <NoLaunchesScheduled onNavigateToSettings={handleNavigateToSettings} />
    )

    const scheduledInfiniteProps = viewingAll
      ? { onScroll: handleScheduledScroll, ...scheduledAllTable.tableProps }
      : {}

    return (
      <DataTableV2
        name="marina-schedule-scheduled"
        columns={scheduledColumns}
        data={scheduledRows}
        idKey="encodedId"
        actions={scheduledActions}
        isLoading={scheduledLoading}
        emptyState={scheduledEmptyState}
        hideSettings
        viewOverride={appliedViewOverride}
        maxHeight={viewingAll ? "calc(100vh - 300px)" : undefined}
        {...scheduledInfiniteProps}
      />
    )
  }

  const renderLaunchedTab = () => {
    if (launchedError)
      return (
        <div className="flex w-full justify-center p-4 lg:justify-start">
          Error loading launches
        </div>
      )

    return (
      <DataTableV2
        name="marina-schedule-launched"
        columns={launchedColumns}
        data={launchedRows}
        actions={launchedActions}
        emptyState={<NoBoatsLaunched />}
        hideSettings
        viewOverride={appliedViewOverride}
        maxHeight="calc(100vh - 300px)"
        onScroll={handleLaunchedScroll}
        {...launchedTable.tableProps}
      />
    )
  }

  const renderReturnedTab = () => {
    if (returnedError)
      return (
        <div className="flex w-full justify-center p-4 lg:justify-start">
          Error loading launches
        </div>
      )

    return (
      <>
        {!returnedLoading && returnedRows.length > 0 && (
          <span className="mb-3 w-full px-4 text-base lg:w-2/3 lg:pl-0 lg:text-sm">
            Showing boats that have been marked as returned to their dry storage
            slip in the past 48 hours.
          </span>
        )}
        <DataTableV2
          name="marina-schedule-returned"
          columns={returnedColumns}
          data={returnedRows}
          idKey="encodedId"
          actions={returnedActions}
          isLoading={returnedLoading}
          emptyState={<NoRecentReturns />}
          hideSettings
          viewOverride={appliedViewOverride}
        />
      </>
    )
  }

  // Context value — used by ScheduleAppointmentModal + LongFormDatePicker
  const contextValue = {
    canManage,
    currentAppointment,
    currentDate,
    currentTabName,
    marinaTimezone,
    setCurrentAppointment,
    setCurrentDate,
    handleLastActionTakenAt,
    trackingProps,
  }

  return (
    <MarinaScheduleContext.Provider value={contextValue}>
      <div className="px-0 pt-6 lg:px-4" id="marina-launch-schedule">
        <Tabs selectedIndex={currentTab} onChange={handleTabChange}>
          <div className="sticky top-0 z-10 bg-white pt-2">
            <div className="flex w-full flex-col items-center justify-between lg:flex-row">
              <div className="order-2 flex justify-center border-b lg:order-1 lg:border-none">
                <Tabs.TabList>
                  <Tabs.Tab title="Scheduled" count={scheduledCount} />
                  <Tabs.Tab title="Launched" count={launchedCount} />
                  <Tabs.Tab title="Returned" count={returnedCount} />
                </Tabs.TabList>
              </div>
              {(showCreateLaunchButton || isLargeScreen) && (
                <div className="lg:px-02 order-1 flex items-center justify-center px-4 pb-8 lg:order-2 lg:mb-0">
                  <div className="mr-2 hidden lg:block">
                    <ViewSwitcher
                      view={activeView}
                      onChange={handleViewSwitch}
                    />
                  </div>
                  {showCreateLaunchButton && (
                    <>
                      <div className="mr-2">
                        <Button fullWidth onClick={handleReportsClick}>
                          View Launch Reports
                        </Button>
                      </div>
                      <Button
                        fullWidth
                        onClick={handleNewLaunchClick}
                        variant="primary"
                      >
                        Schedule New Launch
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <Tabs.Panels>
            <Tabs.Panel>
              <div className="mb-4 grid w-full grid-cols-12 gap-4 px-2 lg:px-0">
                <div className="col-span-12 md:col-span-2">
                  <Form.Select.Custom
                    id="view-select"
                    onChange={handleViewChange}
                    value={currentViewFromParams}
                    label={viewingAll ? "View All" : "Daily View"}
                    showNotificationIndicator={
                      dailyUnviewedScheduledCount > 0 ||
                      allTimeUnviewedScheduledCount > 0
                    }
                  >
                    <Form.Select.RichOption value="daily" hideCheck>
                      {renderViewOption(
                        "Daily View",
                        dailyUnviewedScheduledCount
                      )}
                    </Form.Select.RichOption>
                    <Form.Select.RichOption value="all" hideCheck>
                      {renderViewOption(
                        allScheduledCount > 0
                          ? `View All (${allScheduledCount})`
                          : "View All",
                        allTimeUnviewedScheduledCount
                      )}
                    </Form.Select.RichOption>
                  </Form.Select.Custom>
                </div>
                {!viewingAll && (
                  <div className="col-span-12 text-lg font-semibold md:col-span-10 xl:col-span-4">
                    <LongFormDatePicker
                      parentContext={MarinaScheduleContext}
                      forwardTrackerEvent="dry_stack_schedule_view:scheduled_launches_date_forward_pressed"
                      backTrackerEvent="dry_stack_schedule_view:scheduled_launches_date_back_pressed"
                      selectedTrackerEvent="dry_stack_schedule_view:scheduled_launches_date_selected_pressed"
                      trackingProps={trackingProps}
                    />
                  </div>
                )}
              </div>
              <div className="px-2 lg:px-0">{renderScheduledTab()}</div>
            </Tabs.Panel>
            <Tabs.Panel>
              <div className="px-2 lg:px-0">{renderLaunchedTab()}</div>
            </Tabs.Panel>
            <Tabs.Panel>
              <div className="flex flex-col px-2 lg:px-0">
                {renderReturnedTab()}
              </div>
            </Tabs.Panel>
          </Tabs.Panels>
        </Tabs>
      </div>
      <LaunchFuelQuantityModal
        addons={pendingFuelLaunch?.addons}
        isOpen={Boolean(pendingFuelLaunch)}
        isSubmitting={isBuildingCart}
        onCancel={cancelFuelQuantities}
        onConfirm={confirmFuelQuantities}
      />
      <ScheduleAppointmentModal
        closeModal={() => {
          setModalIsOpen(false)
          setCurrentAppointment(null)
        }}
        currentDate={parseISO(currentDate)}
        isOpen={modalIsOpen}
        marinaSlug={marinaSlug}
      />
    </MarinaScheduleContext.Provider>
  )
}

MarinaScheduleView.propTypes = {
  canManage: PropTypes.bool.isRequired,
  launchAddonsEnabled: PropTypes.bool.isRequired,
  launchCartCopy: PropTypes.shape({
    genericError: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
  marinaTimezone: PropTypes.string.isRequired,
  showCreateLaunchButton: PropTypes.bool.isRequired,
  trackingProps: PropTypes.shape({
    marina_id: PropTypes.string.isRequired,
    marina_name: PropTypes.string.isRequired,
  }),
}

export default MarinaScheduleView
